import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Testimonials() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-200/20 to-emerald-200/20 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative z-10">
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/50 backdrop-blur-sm rounded-full mb-3 sm:mb-4 md:mb-6 border border-blue-200 shadow-sm">
            <i className="ri-chat-quote-line text-white text-xs sm:text-sm md:text-base"></i>
            <span className="text-xs sm:text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('testimonials.badge')}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#0F172A] mb-3 sm:mb-4 md:mb-6 leading-tight px-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('testimonials.title')} <span className="bg-gradient-to-r from-[#10B981] to-[#1E3A8A] bg-clip-text text-transparent animate-pulse">{t('testimonials.titleHighlight')}</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
          {[
            {
              name: t('testimonials.testimonial1.author'),
              role: t('testimonials.testimonial1.role'),
              company: t('testimonials.testimonial1.company'),
              image: 'SM',
              rating: 5,
              text: t('testimonials.testimonial1.text'),
              color: 'from-[#10B981] to-[#1E3A8A]',
              delay: '0ms'
            },
            {
              name: t('testimonials.testimonial2.author'),
              role: t('testimonials.testimonial2.role'),
              company: t('testimonials.testimonial2.company'),
              image: 'MW',
              rating: 5,
              text: t('testimonials.testimonial2.text'),
              color: 'from-[#10B981] to-[#1E3A8A]',
              delay: '200ms'
            },
            {
              name: t('testimonials.testimonial3.author'),
              role: t('testimonials.testimonial3.role'),
              company: t('testimonials.testimonial3.company'),
              image: 'AS',
              rating: 5,
              text: t('testimonials.testimonial3.text'),
              color: 'from-[#10B981] to-[#1E3A8A]',
              delay: '400ms'
            }
          ].map((testimonial, idx) => (
            <div
              key={idx}
              className={`group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:scale-105 transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? testimonial.delay : '0ms' }}
            >
              {/* Stars */}
              <div className="flex gap-0.5 sm:gap-1 mb-4 sm:mb-5 md:mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FABB05] text-base sm:text-lg md:text-xl"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-slate-600 group-hover:text-white leading-relaxed mb-5 sm:mb-6 md:mb-8 text-sm sm:text-base md:text-lg italic transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 sm:gap-4 border-t border-slate-100 group-hover:border-white/20 pt-4 transition-colors">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${testimonial.color} rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-md group-hover:scale-110 transition-transform flex-shrink-0 border border-white/20`}>
                  {testimonial.image}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-[#0F172A] group-hover:text-white text-base sm:text-lg truncate transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                    {testimonial.name}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-500 group-hover:text-emerald-100 truncate transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {testimonial.role}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 group-hover:text-white/70 truncate transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {testimonial.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Bar */}
        <div className={`mt-12 sm:mt-16 md:mt-20 flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-12 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center group">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0F172A] mb-1 sm:mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              500+
            </div>
            <div className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('testimonials.trustRate')}
            </div>
          </div>
          <div className="w-px h-8 sm:h-10 md:h-12 bg-slate-200"></div>
          <div className="text-center group">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0F172A] mb-1 sm:mb-2 group-hover:text-tron-secondary transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              50.000+
            </div>
            <div className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('testimonials.managed')}
            </div>
          </div>
          <div className="w-px h-8 sm:h-10 md:h-12 bg-slate-200"></div>
          <div className="text-center group">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0F172A] mb-1 sm:mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              98%
            </div>
            <div className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('testimonials.satisfaction')}
            </div>
          </div>
          <div className="w-px h-8 sm:h-10 md:h-12 bg-slate-200"></div>
          <div className="text-center group">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0F172A] mb-1 sm:mb-2 group-hover:text-tron-secondary transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              4.9/5
            </div>
            <div className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('testimonials.avgRating')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
